"use client";

import { getCurrencyBasedOnLanguage, getCurrencyCode } from "@/apiUtils/commonUtils";
import { lensConfigurationSteps } from "@/apiUtils/constants";
import { getLensTypeData, getStepData } from "@/apiUtils/LCUtils";
import { endPageTransition, startPageTransition } from "@/apiUtils/pageLoadingUtils";
import { CheckoutResponse, ConfiguratorStepProps, TranslationData } from "@/common/lib/types";
import { getLangFromLocale } from "@/utils/common";
import {
  populateSummary,
  processFilters,
  resetFilters,
  resetSummary,
} from "@/utils/lensConfigurator";
import { useEffect, useMemo, useState } from "react";
import { useConfiguratorContext } from "./context/Provider";
import LensConfiguratorView from "./LensConfiguratorView";

export type LocalAddToCartFuncResponse = {
  success: boolean;
  cartCount: number;
  response: CheckoutResponse;
};

interface IProps {
  locale: string;
  translations: TranslationData;
  isFetchingPrescriptions: boolean;
  onCloseLensConfigurator: () => void;
  handleAddToCart: ConfiguratorStepProps["handleAddToCart"];
}

const LensConfigurator = ({
  locale,
  translations,
  handleAddToCart,
  isFetchingPrescriptions,
  onCloseLensConfigurator,
}: IProps) => {
  const {
    filters,
    summary,
    lensType,
    updateSku,
    setLoading,
    productInfo,
    getNextStep,
    currentStep,
    updateSummary,
    updateLensType,
    handleStepUpdate,
    updateExitModalConfig,
  } = useConfiguratorContext();

  const [stepOptions, setStepOptions] = useState<any>({});
  const [localLoading, setLocalLoading] = useState<boolean>(true);

  const currencyCode = useMemo(() => {
    const currency = getCurrencyCode(locale);
    const language = getLangFromLocale(locale);
    return language === "en" ? currency : getCurrencyBasedOnLanguage(currency);
  }, [locale]);

  const handleLocalStepUpdate = async (step: lensConfigurationSteps, data: any, skip?: number) => {
    setLoading(true);

    const sku = data?.sku || null;

    if (sku) {
      updateSku(sku);
    }

    if (step === lensConfigurationSteps.LENS_TYPE) {
      updateLensType(data.type);
    }

    let nextStep = getNextStep(step, data?.type || lensType, skip);

    const newFilters = processFilters(step, data, filters);
    const stepInfo = await getStepData(
      nextStep,
      newFilters,
      { ...stepOptions, productInfo },
      locale,
    );
    const summaryData = populateSummary(data, step, summary);

    if (newFilters && stepInfo && summaryData) {
      handleStepUpdate(nextStep, { step, ...newFilters });
      setStepOptions({ ...stepInfo, step: nextStep });
      updateSummary(summaryData);
    }

    setLoading(false);
  };

  const handleJumpToStep = async (step: lensConfigurationSteps) => {
    setLoading(true);
    const newFilters = resetFilters(step, filters);
    const stepInfo = await getStepData(step, newFilters, stepOptions, locale);
    const clearedSummary = resetSummary(summary, step);

    handleStepUpdate(step, { step, ...newFilters });
    setStepOptions({ ...stepInfo, step, productInfo });
    updateSummary(clearedSummary);
    updateSku({ left: null, right: null });
    setLoading(false);
  };

  useEffect(() => {
    startPageTransition(false);
    getLensTypeData(locale, {
      data: {},
      productInfo: productInfo ?? {
        name: "",
        category: "",
        productId: "",
        variantId: "",
        variantName: "",
        price: { currency: "", salePrice: 0, listPrice: 0, tax: 0 },
      },
    })
      .then((data) => {
        setStepOptions({ ...data, step: lensConfigurationSteps.LENS_TYPE });
        const { enable, media } = data?.staticContent?.exitLensConfiguratorOverlayImage || {};
        updateExitModalConfig(enable, media);
        setLocalLoading(false);
      })
      .finally(() => endPageTransition());
  }, [locale, updateExitModalConfig, productInfo]);

  return (
    <LensConfiguratorView
      locale={locale}
      currentStep={currentStep}
      stepOptions={stepOptions}
      translations={translations}
      currencyCode={currencyCode}
      handleAddToCart={handleAddToCart}
      handleJumpToStep={handleJumpToStep}
      handleStepData={handleLocalStepUpdate}     
      onCloseLensConfigurator={onCloseLensConfigurator}
      localLoading={localLoading || isFetchingPrescriptions}
    />
  );
};

export default LensConfigurator;
